import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ClipboardList, FileCode, Hammer, FlaskConical,
  Rocket, Undo2, Sparkles, Activity,
} from 'lucide-react';

const STAGES = [
  { key: 'plans',        label: 'PLAN',      icon: ClipboardList, count: 'plans',               tab: 'planner'   },
  { key: 'diffs',        label: 'DIFF',      icon: FileCode,      count: 'diffs',               tab: 'codediffs' },
  { key: 'previews',     label: 'PREVIEW',   icon: Hammer,        count: 'previews',            tab: 'preview'   },
  { key: 'tests',        label: 'TESTS',     icon: FlaskConical,  count: 'test_runs',           tab: 'tests'     },
  { key: 'deploys',      label: 'DEPLOY',    icon: Rocket,        count: 'deployment_requests', tab: 'deploy'    },
  { key: 'rollbacks',    label: 'ROLLBACK',  icon: Undo2,         count: 'rollback_requests',   tab: 'rollback'  },
];

const SEV_COLOR = {
  high:   'text-sell',
  medium: 'text-hold',
  low:    'text-cyan-sys',
};

/**
 * CommandCenterSection — Phase 51.I Lab section.
 * A unified live snapshot of the whole engineering pipeline: recent
 * items per stage + open improvement suggestions + production deploy
 * history. Polls the aggregation endpoint every 20s.
 */
export default function CommandCenterSection({ onSwitchTab }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    return api.get('/lab/command/summary')
      .then(({ data }) => setSummary(data))
      .catch(() => toast.error('Could not load command summary'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  if (!summary && loading) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-[var(--txt-secondary)] p-6">
        <Loader2 size={12} className="animate-spin" /> loading pipeline snapshot…
      </div>
    );
  }
  if (!summary) return null;

  const { counts, recent, improvements } = summary;

  return (
    <div data-testid="lab-command" className="space-y-3">
      {/* HEADER */}
      <div className="border border-cyan-sys p-3 flex items-center justify-between gap-2">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-1">
            // command center — live pipeline
          </div>
          <div className="text-[10px] text-[var(--txt-secondary)]">
            Auto-refreshes every 20s. Click any stage to jump to its detail tab.
          </div>
        </div>
        <button
          data-testid="lab-command-refresh"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 border border-cyan-sys text-cyan-sys px-2 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black disabled:opacity-30"
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          refresh
        </button>
      </div>

      {/* PIPELINE STAGES */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {STAGES.map((s) => {
          const items = recent[s.key] || [];
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              data-testid={`lab-command-stage-${s.key}`}
              onClick={() => onSwitchTab?.(s.tab)}
              className="text-left border border-[var(--border-default)] hover:border-cyan-sys p-2 space-y-1.5 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-[9px] tracking-[0.25em] uppercase text-cyan-sys">
                  <Icon size={11} /> {s.label}
                </div>
                <span className="text-[11px] font-mono text-[var(--txt-primary)]">
                  {counts[s.count] ?? 0}
                </span>
              </div>
              <div className="space-y-0.5">
                {items.slice(0, 3).map((it) => (
                  <div key={it.id} className="text-[9px] font-mono text-[var(--txt-secondary)] truncate">
                    {(it.id || '').slice(0, 8)}
                    {it.status && <> · <span className="text-[var(--txt-primary)]">{it.status}</span></>}
                  </div>
                ))}
                {items.length === 0 && (
                  <div className="text-[9px] font-mono text-[var(--txt-muted)]">— empty —</div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* PRODUCTION DEPLOY HISTORY + IMPROVEMENTS */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="border border-[var(--border-default)] p-3">
          <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-2 flex items-center gap-1.5">
            <Activity size={11} /> production deploys ({counts.prod_deploys})
          </div>
          {(recent.prod_deploys || []).length === 0 ? (
            <div className="text-[10px] text-[var(--txt-secondary)]">
              No production deploys recorded.
            </div>
          ) : (
            <ul className="space-y-1">
              {recent.prod_deploys.slice(0, 5).map((d) => (
                <li key={d.id || d.sha} className="text-[10px] font-mono flex items-center justify-between">
                  <span className="truncate">
                    <span className={d.status === 'success' ? 'text-buy' : 'text-sell'}>
                      {d.sha}
                    </span>
                    {' '}· {(d.deployed_at || '').slice(0, 19).replace('T', ' ')}Z
                  </span>
                  <span className="text-[9px] uppercase tracking-[0.15em] text-[var(--txt-secondary)]">
                    {d.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border border-[var(--border-default)] p-3">
          <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Sparkles size={11} /> open improvements ({counts.open_suggestions})
            </span>
            <button
              onClick={() => onSwitchTab?.('improve')}
              className="text-cyan-sys hover:text-buy text-[9px] uppercase tracking-[0.2em]"
              data-testid="lab-command-goto-improve"
            >
              view all →
            </button>
          </div>
          <div className="flex gap-3 mb-2 text-[10px] font-mono">
            <span className={SEV_COLOR.high}>high {improvements.by_severity.high || 0}</span>
            <span className={SEV_COLOR.medium}>med {improvements.by_severity.medium || 0}</span>
            <span className={SEV_COLOR.low}>low {improvements.by_severity.low || 0}</span>
          </div>
          {(improvements.top || []).length === 0 ? (
            <div className="text-[10px] text-[var(--txt-secondary)]">
              No open suggestions. Run a scan in the SELF-IMPROVE tab.
            </div>
          ) : (
            <ul className="space-y-1">
              {improvements.top.slice(0, 6).map((s) => (
                <li key={s.id} className="text-[10px] font-mono flex items-start gap-2">
                  <span className={`text-[9px] uppercase tracking-[0.15em] shrink-0 ${SEV_COLOR[s.severity]}`}>
                    {s.severity[0]}
                  </span>
                  <span className="text-[var(--txt-primary)] truncate">{s.title}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
